import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, Send } from "lucide-react";

export default function EmailFeed() {
  const [emails, setEmails] = useState([]);
  const [form, setForm] = useState({ from: "", subject: "", text: "" });
  const [settings, setSettings] = useState(null);
  const [gmailForm, setGmailForm] = useState({ gmail_address: "", app_password: "" });
  const [testing, setTesting] = useState(false);
  const [simChannel, setSimChannel] = useState("email");

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/inbound-emails"),
        api.get("/settings/email-polling"),
      ]);
      setEmails(r.data);
      setSettings(s.data);
      if (s.data.gmail_address && !gmailForm.gmail_address) {
        setGmailForm((f) => ({ ...f, gmail_address: s.data.gmail_address }));
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings/email-polling", { ...gmailForm, enabled: true });
      toast.success("Gmail inbox connected");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const testNow = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/email-polling/test");
      if (r.data.error) {
        toast.error(`Connection failed: ${r.data.error}`);
      } else if (r.data.skipped) {
        toast.error("Save your Gmail settings first");
      } else {
        toast.success(r.data.count > 0 ? `${r.data.count} ticket(s) created: ${r.data.created.join(", ")}` : "Connected — no new emails right now");
      }
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setTesting(false);
    }
  };

  const simulate = async (e) => {
    e.preventDefault();
    try {
      const headers = { "Content-Type": "multipart/form-data", Authorization: undefined };
      if (simChannel === "email") {
        const fd = new FormData();
        fd.append("from", form.from);
        fd.append("to", "support@yourdomain.com");
        fd.append("subject", form.subject);
        fd.append("text", form.text);
        const r = await api.post("/webhooks/inbound-email", fd, { headers });
        toast.success(`Ticket ${r.data.ticket.number} ${r.data.threaded ? "updated" : "created"} from email`);
      } else {
        const fd = new FormData();
        fd.append("From", `whatsapp:${form.from}`);
        fd.append("Body", form.text);
        fd.append("ProfileName", "WhatsApp Client");
        const r = await api.post("/webhooks/inbound-whatsapp", fd, { headers });
        toast.success(`Ticket ${r.data.ticket_number} ${r.data.threaded ? "updated" : "created"} from WhatsApp`);
      }
      setForm({ from: "", subject: "", text: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="email-feed-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Email → Ticket</h1>
        <p className="text-sm text-muted-foreground mt-1">Any email sent to your support Gmail inbox becomes a ticket within a minute — automatically.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-semibold">Connect your Gmail support inbox</h2>
          {settings?.configured && (
            <span data-testid="gmail-status" className={`font-mono-digits text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${
              settings.last_error ? "text-red-400 border-red-500/30 bg-red-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
            }`}>
              {settings.last_error ? "Error" : "Connected"}
            </span>
          )}
        </div>
        <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside leading-relaxed">
          <li>Create or pick a Gmail address for support, e.g. <span className="font-mono-digits text-foreground">narkitsupport@gmail.com</span> — customers email this address.</li>
          <li>In that Google account: <span className="text-foreground">myaccount.google.com → Security → 2-Step Verification (turn on) → App passwords</span> → create one named "Chronodesk".</li>
          <li>Paste the address and the 16-character app password below. The app checks the inbox every minute and turns each new email into a ticket.</li>
        </ol>
        <form data-testid="gmail-settings-form" onSubmit={saveSettings} className="grid sm:grid-cols-2 gap-4">
          <input required type="email" placeholder="Gmail address" value={gmailForm.gmail_address}
            onChange={(e) => setGmailForm({ ...gmailForm, gmail_address: e.target.value })}
            data-testid="gmail-address-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <input required type="password" placeholder="App password (16 chars)" value={gmailForm.app_password}
            onChange={(e) => setGmailForm({ ...gmailForm, app_password: e.target.value })}
            data-testid="gmail-app-password-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <div className="sm:col-span-2 flex gap-3 flex-wrap">
            <button type="submit" data-testid="gmail-save-button"
              className="rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2 transition-all">
              Save & connect
            </button>
            <button type="button" onClick={testNow} disabled={testing} data-testid="gmail-test-button"
              className="rounded-md border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-50">
              {testing ? "Checking…" : "Check inbox now"}
            </button>
          </div>
        </form>
        {settings?.configured && (
          <p className="text-xs text-muted-foreground" data-testid="gmail-last-poll">
            {settings.last_error
              ? <span className="text-red-400">Last error: {settings.last_error}</span>
              : settings.last_poll_at
                ? <>Last checked {new Date(settings.last_poll_at).toLocaleString()}{settings.last_created?.length > 0 && <> — created {settings.last_created.join(", ")}</>}</>
                : "Waiting for first check…"}
          </p>
        )}
        <p className="text-xs text-muted-foreground">Tip: ask your registrar to remove the MX record pointing to mx.sendgrid.net — it's no longer needed. The simulator below still works for manual testing.</p>
      </div>

      <form data-testid="email-simulator-form" onSubmit={simulate} className="rounded-lg border border-orange-500/30 bg-card p-6 space-y-4">
        <h2 className="text-lg font-semibold">Simulate inbound message</h2>
        <div className="flex gap-2">
          <button type="button" data-testid="sim-channel-email" onClick={() => setSimChannel("email")}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
              simChannel === "email" ? "bg-sky-500/15 text-sky-400 border-sky-500/40" : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            <Mail className="h-3.5 w-3.5" /> Email
          </button>
          <button type="button" data-testid="sim-channel-whatsapp" onClick={() => setSimChannel("whatsapp")}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
              simChannel === "whatsapp" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input required placeholder={simChannel === "email" ? "From (e.g. Jane Doe <jane@acme.com>)" : "WhatsApp number (e.g. +27821234567)"} value={form.from}
            onChange={(e) => setForm({ ...form, from: e.target.value })}
            data-testid="sim-from-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          {simChannel === "email" && (
            <input required placeholder="Subject" value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="sim-subject-input"
              className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          )}
        </div>
        <textarea required rows={4} placeholder="Email body…" value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          data-testid="sim-body-input"
          className="w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
        <button type="submit" data-testid="simulate-email-webhook-button"
          className="flex items-center gap-2 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2.5 transition-all">
          <Send className="h-4 w-4" /> Send as inbound email
        </button>
      </form>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Received emails</h2>
        {emails.length === 0 && (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">No inbound emails yet.</p>
        )}
        {emails.map((m) => (
          <div key={m.id} data-testid={`inbound-email-${m.id}`}
            className="rounded-lg border border-border bg-card px-4 py-3.5 hover:border-orange-500/40 transition-colors">
            <div className="flex items-center gap-3 flex-wrap">
              <Mail className="h-4 w-4 text-sky-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{m.subject}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{m.from} · {new Date(m.received_at).toLocaleString()}</p>
              </div>
              <Link to={`/tickets/${m.ticket_id}`} data-testid={`inbound-ticket-link-${m.id}`}
                className="font-mono-digits text-xs text-orange-400 hover:underline">
                {m.ticket_number}
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 whitespace-pre-wrap">{m.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
